import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const money = n => (n === null || n === undefined || n === '') ? '—' : '$' + Number(n).toFixed(2);

/**
 * Products tab → "From suppliers": every linked supplier's products in one
 * searchable list (the Products tab's own search box drives it), with the same
 * price / put-in / take-out / delete controls as the Suppliers tab.
 */
function TelegramSupplierProducts({ search = '', onChanged, onPromote }) {
    const [rows, setRows] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [priceDraft, setPriceDraft] = useState({});
    const [shown, setShown] = useState(60);
    const [msg, setMsg] = useState(null);

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    const load = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/supplier-products');
        if (ok) setRows(body.products || []);
        setLoaded(true);
    }, [authFetch]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setShown(60); }, [search]);

    const flash = (text, bad = false) => { setMsg({ text, bad }); setTimeout(() => setMsg(null), 5000); };

    const save = async (p, patch) => {
        const { ok, body } = await authFetch(`/admin/telegram/supplier-products/${p.id}`, { method: 'PUT', body: JSON.stringify(patch) });
        if (!ok) { flash(body.error || 'Could not save.', true); return false; }
        setRows(rs => rs.map(r => r.id === p.id ? { ...r, ...body.product } : r));
        if (onChanged) onChanged();
        return true;
    };

    const commitPrice = async (p) => {
        const raw = priceDraft[p.id];
        if (raw === undefined) return;
        const v = Number(raw);
        const clear = () => setPriceDraft(d => { const n = { ...d }; delete n[p.id]; return n; });
        if (!(v > 0)) { flash('Enter a price above zero.', true); return; }
        if (Math.abs(v - Number(p.sellUsd)) < 0.001) { clear(); return; }
        if (v <= p.costUsd && !window.confirm(`$${v.toFixed(2)} is not above the supplier cost of $${p.costUsd.toFixed(2)} — the bot will NOT sell it at this price. Save anyway?`)) return;
        if (await save(p, { sellUsd: v })) { clear(); flash(`${p.name}: now ${money(v)}`); }
    };

    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    // Spaces are ignored, so "18Months" finds "18 Months" and "18 MONTHS".
    const hay = r => { const t = `${r.name} ${r.supplierName}`.toLowerCase(); return t + ' ' + t.replace(/\s+/g, ''); };
    const visible = rows.filter(r => (showDeleted || !r.deleted) && words.every(w => hay(r).includes(w)));

    const status = r => {
        if (r.deleted) return ['Deleted', '#dc2626'];
        if (r.gone) return ['Removed by supplier', '#9ca3af'];
        if (!r.supplierEnabled) return ['Supplier paused', '#6b7280'];
        if (!r.listed) return ['Not in bot', '#6b7280'];
        if (r.belowCost) return ['Price ≤ cost', '#d97706'];
        if (!r.inStock) return ['Sold out', '#dc2626'];
        return ['Live in bot', '#16a34a'];
    };

    if (!loaded) return <div className="tg-empty">Loading supplier products…</div>;
    if (!rows.length) return <div className="tg-empty">No supplier linked yet — add one in the <b>Suppliers</b> tab.</div>;

    return (
        <div className="tg-card">
            {msg && <div className={`tg-note ${msg.bad ? 'bad' : 'ok'}`}>{msg.text}</div>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <span><b>{visible.length}</b> supplier product{visible.length === 1 ? '' : 's'}{search.trim() ? ` matching “${search.trim()}”` : ''}</span>
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, margin: 0, fontWeight: 400 }}>
                    <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} /> show deleted
                </label>
                <span style={{ fontSize: 12, opacity: 0.7 }}>Bulk actions and markups: <b>Suppliers</b> tab.</span>
            </div>
            {!visible.length ? <div className="tg-empty">No supplier product matches.</div> : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="tg-table">
                        <thead>
                            <tr><th>Product</th><th>Supplier</th><th>Cost</th><th>Sell</th><th>Profit</th><th>Stock</th><th>Status</th><th></th></tr>
                        </thead>
                        <tbody>
                            {visible.slice(0, shown).map(r => {
                                const typed = priceDraft[r.id];
                                const sell = typed !== undefined ? Number(typed) : r.sellUsd;
                                const profit = sell > 0 ? sell - r.costUsd : null;
                                const [label, color] = status(r);
                                return (
                                    <tr key={r.id} style={{ opacity: r.deleted || r.gone ? 0.55 : 1 }}>
                                        <td style={{ minWidth: 220, fontWeight: 600 }}>{r.name}</td>
                                        <td>{r.supplierName}</td>
                                        <td>{money(r.costUsd)}</td>
                                        <td>
                                            <input className="tg-input" style={{ width: 82 }} type="number" min="0" step="0.01"
                                                value={typed !== undefined ? typed : (r.sellUsd ?? '')}
                                                onChange={e => setPriceDraft(d => ({ ...d, [r.id]: e.target.value }))}
                                                onBlur={() => commitPrice(r)}
                                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }} />
                                        </td>
                                        <td style={{ color: profit !== null && profit <= 0 ? '#dc2626' : undefined, fontWeight: 600 }}>
                                            {profit === null ? '—' : `${profit < 0 ? '-' : ''}$${Math.abs(profit).toFixed(2)}`}
                                        </td>
                                        <td>{!r.inStock ? '0' : (r.stock > 0 ? r.stock : '∞')}</td>
                                        <td><span className="tg-tag" style={{ color, borderColor: color }}>{label}</span></td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            {r.deleted
                                                ? <button className="tg-btn sm" onClick={() => save(r, { deleted: false, listed: true })}>Restore</button>
                                                : <>
                                                    {onPromote && label === 'Live in bot' && (
                                                        <><button className="tg-btn sm primary" title="Announce this product (optionally at a lower price) to every buyer"
                                                            onClick={() => onPromote(r)}>📣 Promote</button>{' '}</>
                                                    )}
                                                    <button className="tg-btn sm" onClick={() => save(r, { listed: !r.listed })}>{r.listed ? 'Take out' : 'Put in bot'}</button>{' '}
                                                    <button className="tg-btn sm" onClick={() => save(r, { deleted: true })}>Delete</button>
                                                </>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {visible.length > shown && (
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                    <button className="tg-btn" onClick={() => setShown(n => n + 60)}>Show more ({visible.length - shown} left)</button>
                </div>
            )}
        </div>
    );
}

export default TelegramSupplierProducts;
